import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import CustomerHeader from '../../components/ui/CustomerHeader';
import Footer from '../../components/ui/Footer';
import AuthenticationModal from '../../components/ui/AuthenticationModal';
import CartSidebar from '../../components/ui/CartSidebar';
import FilterSidebar from './components/FilterSidebar';
import SearchBar from './components/SearchBar';
import SortAndViewControls from './components/SortAndViewControls';
import ProductGrid from './components/ProductGrid';
import Button from '../../components/ui/Button';
import { getProducts, fromWcProduct, getCategories } from '../../lib/wp-api';
import Pagination from './components/Pagination';

const ProductCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const cart = useCart();
  const wishlist = useWishlist();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(3);
  const [isLoading, setIsLoading] = useState(true);
  const [displayedCount, setDisplayedCount] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Filter and search state
  const [filters, setFilters] = useState({
    categories: [],
    brands: [],
    ratings: [],
    sizes: [],
    priceRange: [0, 200]
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock product data - expanded to show Load More functionality
  const [products, setProducts] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);

  useEffect(() => {
    let cancelled = false;
    // Server-side fetch: WC does the filtering by category AND search term
    // (it indexes product names + descriptions). We debounce search so each
    // keystroke doesn't fire a request — wait 400ms after typing stops.
    const params = { per_page: 100 };
    const selected = filters?.categories || [];
    if (selected.length > 0) params.category = selected.join(',');
    const term = (searchTerm || '').trim();
    if (term) params.search = term;
    const fire = () => {
      setIsLoading(true);
      getProducts(params)
        .then((rows) => { if (!cancelled) setProducts(rows.map(fromWcProduct)); })
        .catch(() => { if (!cancelled) setProducts([]); })
        .finally(() => { if (!cancelled) setIsLoading(false); });
    };
    // Debounce only when there's a search term; immediate when search is empty
    const timer = term ? setTimeout(fire, 400) : (fire(), null);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [filters?.categories, searchTerm]);

  // Load the category tree so the filter logic can expand a selected parent
  // (e.g. "Dresses" id=16) to include products tagged with its children
  // (Mini Dress id=27, Midi Dress id=28, Maxi Dress id=31, ...). WC tags
  // products with leaf categories only.
  useEffect(() => {
    let cancelled = false;
    getCategories()
      .then((tree) => { if (!cancelled) setCategoryTree(tree); })
      .catch(() => { if (!cancelled) setCategoryTree([]); });
    return () => { cancelled = true; };
  }, []);

  // Expand each selected category id to {self + all descendants}
  const expandedCategoryIds = useMemo(() => {
    const set = new Set(filters?.categories || []);
    if (!filters?.categories?.length || !categoryTree?.length) return set;
    const addDescendants = (node) => {
      if (!node?.children?.length) return;
      for (const child of node.children) {
        set.add(child.id);
        addDescendants(child);
      }
    };
    const findById = (nodes, id) => {
      for (const n of nodes || []) {
        if (n.id === id) return n;
        const f = findById(n.children, id);
        if (f) return f;
      }
      return null;
    };
    for (const id of filters.categories) {
      const node = findById(categoryTree, id);
      if (node) addDescendants(node);
    }
    return set;
  }, [filters?.categories, categoryTree]);



  // Reset to first page when filters/sort/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy, searchTerm]);

  // Filter products based on current filters and search
  const filteredProducts = products?.filter((product) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      if (!product?.name?.toLowerCase()?.includes(searchLower) &&
      !product?.brand?.toLowerCase()?.includes(searchLower) &&
      !product?.description?.toLowerCase()?.includes(searchLower)) {
        return false;
      }
    }

    // Category filter — match by category ID. fromWcProduct now emits
    // category IDs (numbers). The selected filter IDs are the same numeric
    // ids FilterSidebar checkboxes carry. expandedCategoryIds covers
    // {selected + all descendants} so picking a parent matches its leaves.
    // Category filter: SERVER-side already filtered (see useEffect above). This
    // client-side check is a no-op safety net for cases where products were
    // fetched without a category param (initial unfiltered load + then the
    // selection changed before the refetch completed).
    if (filters?.categories?.length > 0) {
      const productCatIds = Array.isArray(product?.categories) ? product.categories : [];
      if (!productCatIds.some((id) => expandedCategoryIds.has(id))) return false;
    }

    // Brand filter
    if (filters?.brands?.length > 0) {
      const brandId = product?.brand?.toLowerCase()?.replace(/\s+/g, '-');
      if (!filters?.brands?.includes(brandId)) {
        return false;
      }
    }

    // Price range filter
    if (product?.price < filters?.priceRange?.[0] || product?.price > filters?.priceRange?.[1]) {
      return false;
    }

    // Rating filter
    if (filters?.ratings?.length > 0) {
      const hasMatchingRating = filters?.ratings?.some((rating) => {
        switch (rating) {
          case '4-plus':return product?.rating >= 4;
          case '3-plus':return product?.rating >= 3;
          case '2-plus':return product?.rating >= 2;
          case '1-plus':return product?.rating >= 1;
          default:return true;
        }
      });
      if (!hasMatchingRating) {
        return false;
      }
    }

    // Size filter — FilterSidebar pushes lowercase ids ('s','m','l'); product
    // sizes come from WC attributes as uppercase ('S','M','XL'). Normalize.
    if (filters?.sizes?.length > 0) {
      const productSizes = (product?.sizes || []).map((s) => String(s).toUpperCase());
      const selectedSizes = filters.sizes.map((s) => String(s).toUpperCase());
      if (!selectedSizes.some((s) => productSizes.includes(s))) {
        return false;
      }
    }

    return true;
  });

  // Sort products
  const sortedProducts = [...filteredProducts]?.sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a?.price - b?.price;
      case 'price-high':
        return b?.price - a?.price;
      case 'rating':
        return b?.rating - a?.rating;
      case 'newest':
        return b?.isNew ? 1 : -1;
      case 'bestseller':
        return b?.isBestseller ? 1 : -1;
      case 'name-az':
        return a?.name?.localeCompare(b?.name);
      default:
        return 0;
    }
  });

  // Pagination (client-side over the fetched + filtered + sorted set)
  const totalProducts = sortedProducts?.length;
  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedProducts = sortedProducts?.slice(startIndex, startIndex + itemsPerPage);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      categories: [],
      brands: [],
      ratings: [],
      sizes: [],
      priceRange: [0, 200]
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  };

  const handleAddToCart = (product) => {
    if (!product) return;
    cart.addItem({
      id: product.id,
      name: product.name,
      price: product.price ?? 0,
      image: product.image,
      quantity: 1,
    });
  };

  const handleToggleWishlist = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    wishlist.toggle(product);
  };

  const handleAuthentication = (authData) => {
    console.log('User authenticated:', authData);
    setShowAuthModal(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <CustomerHeader
        onCartClick={() => setShowCartSidebar(true)} />


      {/* Main Content */}
      <div className="pt-20 pb-16 flex-1">
        <div className="w-full">
          <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
            {/* Filter Sidebar */}
            <FilterSidebar
              isOpen={showFilterSidebar}
              onClose={() => setShowFilterSidebar(false)}
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              products={products} />


            {/* Main Content Area */}
            <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8">
              {/* Page Header */}
              <div className="py-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
                  <div>
                    <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">
                      Fashion Items
                    </h1>
                    <p className="text-muted-foreground">
                      Discover premium fashion items from top brands
                    </p>
                  </div>

                  {/* Mobile Filter Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowFilterSidebar(true)}
                    iconName="Filter"
                    iconPosition="left"
                    className="lg:hidden">

                    Filters
                  </Button>
                </div>

                {/* Search Bar */}
                <div className="mb-8">
                  <SearchBar onSearch={handleSearch} />
                </div>

                {/* Sort and View Controls */}
                <SortAndViewControls
                  sortBy={sortBy}
                  onSortChange={handleSortChange}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  totalResults={totalProducts}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage} />

              </div>

              {/* Product Grid */}
              <div className="mb-8">
                <ProductGrid
                  products={displayedProducts}
                  viewMode={viewMode}
                  onAddToCart={handleAddToCart}
                  onToggleWishlist={handleToggleWishlist}
                  isLoading={isLoading}
                  onClearFilters={handleClearFilters} />

              </div>

              {/* Pagination */}
              <div className="mb-12">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalProducts}
                  onItemsPerPageChange={handleItemsPerPageChange} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Modals and Sidebars */}
      <AuthenticationModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthenticate={handleAuthentication}
        initialMode="login" />


      <CartSidebar
        isOpen={showCartSidebar}
        onClose={() => setShowCartSidebar(false)}
        cartItems={[]}
        onUpdateQuantity={() => {}}
        onRemoveItem={() => {}}
        onCheckout={() => {}} />

    </div>);

};

export default ProductCatalog;